import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { faqs, siteInfo } from "@/mocks/site";

export default function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  useSeo({
    title: "常见问题 - 樱花动漫社使用说明",
    description:
      "樱花动漫社常见问题解答：本站是否提供在线播放、作品库如何筛选、专栏更新节奏、资料更正与内容引用方式，帮助你快速了解如何使用本站。",
    keywords: "常见问题,樱花动漫社,使用说明,动漫网站FAQ",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${siteInfo.domain}/faq` },
        ],
      },
    ],
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "常见问题" }]} />

      <div className="mt-6">
        <h1 className="font-heading text-3xl font-700 tracking-tight text-foreground-950 md:text-4xl">
          常见问题
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          这里整理了关于使用本站最常被问到的问题。如果没有找到答案，欢迎直接发邮件给我们。
        </p>
      </div>

      <div className="mt-10 divide-y divide-background-200 overflow-hidden rounded-xl border border-background-200 bg-background-50">
        {faqs.map((item, index) => {
          const isOpen = open === index;
          return (
            <div key={item.q}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-100"
              >
                <span className="font-heading text-base font-600 text-foreground-950">{item.q}</span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} aria-hidden="true"></i>
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-7 text-foreground-600 md:text-base">{item.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 rounded-2xl bg-secondary-50 p-8 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-50 text-secondary-700">
          <i className="ri-question-line text-xl" aria-hidden="true"></i>
        </span>
        <h2 className="mt-4 font-heading text-xl font-700 text-foreground-950">还有别的问题？</h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          可以通过邮箱联系编辑部，我们会在核对后尽快回复。
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={`mailto:${siteInfo.email}`}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-mail-send-line" aria-hidden="true"></i>
            {siteInfo.email}
          </a>
          <Link
            to="/works"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-500 text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-stack-line" aria-hidden="true"></i>
            去作品库看看
          </Link>
        </div>
      </div>
    </div>
  );
}